// Shared Collabora iframe session: dedicated WOPI token at open, postMessage host, token refresh, timer fallback, remount.

import { useCallback, useEffect, useRef, useState } from 'react';

import {
  DEADLINE_LEAD_MS,
  FALLBACK_LEAD_MS,
  isFresh,
  isTrustedMessage,
  parseCollaboraMessage,
  postToCollabora,
} from 'services/auth/collabora-messages';
import { getValidAccessToken } from 'services/auth/token-store';
import { buildCollaboraUrl, collaboraOrigin } from 'services/auth/wopi-url';
import { mintWopiToken } from 'services/auth/wopi';
import type { WopiToken } from 'services/auth/wopi';

export type CollaboraStatus = 'loading' | 'ready' | 'reauth';

export type CollaboraSession = {
	status: CollaboraStatus;
	// Null until the first mint succeeds; kept after a later refresh failure so unsaved edits are not destroyed.
	src: string | null;
	frameKey: number;
	iframeRef: React.MutableRefObject<HTMLIFrameElement | null>;
	retry: () => void;
};

type ViewState = { status: CollaboraStatus; src: string | null; frameKey: number };

type Core = {
	held: WopiToken | null; // token Collabora currently has (URL or successful Reset_Access_Token)
	latest: WopiToken | null; // most recent successful mint
	posted: boolean; // whether `latest` reached the iframe
	remounts: number;
	refreshing: Promise<void> | null;
	fallback: ReturnType<typeof setTimeout> | undefined;
	deadline: ReturnType<typeof setTimeout> | undefined;
	disposed: boolean;
};

// setTimeout overflows (and fires immediately) past 2^31-1 ms.
const MAX_TIMER_MS = 2_147_483_647;

const INITIAL: ViewState = { status: 'loading', src: null, frameKey: 0 };

export const useCollaboraSession = (
  fileId: string | null | undefined,
  enabled: boolean = true,
  onClose?: () => void,
): CollaboraSession => {
  const [view, setView] = useState<ViewState>(INITIAL);
  const [attempt, setAttempt] = useState(0);
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const retryRef = useRef<() => void>(() => { });
  // Ref keeps the session effect stable when consumers pass an inline close handler.
  const onCloseRef = useRef<(() => void) | undefined>(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!enabled || !fileId) {
      setView(INITIAL);
      return undefined;
    }
    const core: Core = {
      held: null, latest: null, posted: false, remounts: 0, refreshing: null, fallback: undefined, deadline: undefined, disposed: false,
    };
    setView(INITIAL);

    const markReauth = () => setView((current) => ({ ...current, status: 'reauth' }));

    const deliver = (token: WopiToken): void => {
      try {
        postToCollabora(iframeRef.current, collaboraOrigin(), 'Reset_Access_Token', { token: token.accessToken, ttl: token.ttl });
      } catch {
        // Collabora sends no ack: a throw (or no frame) is the only signal. Stay undelivered; the deadline remounts.
        core.posted = false;
        return;
      }
      core.posted = true;
      core.held = token;
      // Circular by design: deliver → armTimers → refresh → deliver; function declarations make the order hoist-safe.
      // eslint-disable-next-line @typescript-eslint/no-use-before-define
      armTimers(token);
      setView((current) => ({ ...current, status: 'ready' }));
    };

    // Refresh order: (1) session via the existing single-flight, (2) single-flight mint, (3) post ONLY on a 200 mint.
    const refresh = (): Promise<void> => {
      if (core.refreshing) return core.refreshing;
      if (isFresh(core.latest) && core.posted) return Promise.resolve();
      const run = async (): Promise<void> => {
        if (core.latest && isFresh(core.latest)) {
          deliver(core.latest);
          return;
        }
        try {
          await getValidAccessToken();
          const minted = await mintWopiToken(fileId);
          if (core.disposed) return;
          core.latest = minted;
          core.posted = false;
          deliver(minted);
        } catch {
          // Not posted; `latest` stays not-fresh so the next warning retries.
          if (!core.disposed) markReauth();
        }
      };
      core.refreshing = run().finally(() => {
        core.refreshing = null;
      });
      return core.refreshing;
    };

    const onDeadline = (): void => {
      const { held, latest } = core;
      // Only a newer, still-usable 200 mint whose post threw / was never made justifies a remount.
      if (!held || !latest || core.posted || latest.ttl <= held.ttl || latest.ttl <= Date.now() + DEADLINE_LEAD_MS) return;
      if (core.remounts >= 1) {
        markReauth();
        return;
      }
      core.remounts += 1;
      core.held = latest;
      core.posted = true;
      setView((current) => ({ status: 'ready', src: buildCollaboraUrl(fileId, latest), frameKey: current.frameKey + 1 }));
      // eslint-disable-next-line @typescript-eslint/no-use-before-define
      armTimers(latest);
    };

    function armTimers(held: WopiToken): void {
      clearTimeout(core.fallback);
      clearTimeout(core.deadline);
      const now = Date.now();
      core.fallback = setTimeout(() => { refresh(); }, Math.min(MAX_TIMER_MS, Math.max(0, held.ttl - FALLBACK_LEAD_MS - now)));
      core.deadline = setTimeout(onDeadline, Math.min(MAX_TIMER_MS, Math.max(0, held.ttl - DEADLINE_LEAD_MS - now)));
    }

    const onMessage = (event: MessageEvent): void => {
      if (!isTrustedMessage(event, iframeRef.current, collaboraOrigin())) return;
      const message = parseCollaboraMessage(event.data);
      if (message === null) return;
      if (message.messageId === 'UI_Close' || message.messageId === 'close') {
        onCloseRef.current?.();
      } else if (message.messageId === 'App_LoadingStatus' && message.values.Status === 'Document_Loaded') {
        try {
          postToCollabora(iframeRef.current, collaboraOrigin(), 'Host_PostmessageReady', {});
        } catch {
          // Frame went away between the trust check and the post: nothing to ready.
        }
      } else if (message.messageId === 'App_TokenExpiring' || message.messageId === 'App_TokenExpired') {
        refresh();
      }
    };
    window.addEventListener('message', onMessage);

    retryRef.current = () => {
      if (core.held) refresh();
      else setAttempt((value) => value + 1);
    };

    mintWopiToken(fileId).then((token) => {
      if (core.disposed) return;
      core.held = token;
      core.latest = token;
      core.posted = true;
      setView({ status: 'ready', src: buildCollaboraUrl(fileId, token), frameKey: 0 });
      armTimers(token);
    }, () => {
      // Hard mint failure: re-auth state, no iframe URL, session token never used as a fallback.
      if (!core.disposed) setView({ status: 'reauth', src: null, frameKey: 0 });
    });

    return () => {
      core.disposed = true;
      clearTimeout(core.fallback);
      clearTimeout(core.deadline);
      window.removeEventListener('message', onMessage);
    };
  }, [fileId, enabled, attempt]);

  const retry = useCallback(() => retryRef.current(), []);

  return {
    status: view.status, src: view.src, frameKey: view.frameKey, iframeRef, retry,
  };
};
